/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { Modal, Button, Icon, Spinner, ToggleControl, Card, CardHeader, CardBody, CardFooter, ExternalLink } from '@wordpress/components';
import { useState, useEffect } from '@wordpress/element';

/**
 * External dependencies.
 */
import classnames from 'classnames';
import { findKey, remove } from 'lodash';

const AlertKitApply = ( props ) => {
	const { onCancel, onApply, kit } = props;
	const [ { isSuccess, isApplied, isLoading, activeParts, activeTemplates, activeDemoPages, activeSettings, activeStyle }, setState ] = useState( {
		isSuccess: false,
		isApplied: false,
		activeParts: [],
		activeTemplates: [],
		activeDemoPages: [],
		activeStyle: false,
		activeSettings: false,
	} );
	const { kit_parts: kitParts, kit_templates: kitTemplates, kit_demo_pages: kitDemoPages, kit_styles: kitStyles, kit_settings: kitSettings } = kit;

	const updateState = ( data ) => {
		setState( ( prevState ) => {
			return {
				...prevState,
				...data,
			};
		} );
	};

	useEffect( () => {
		let _parts = [];
		if ( kitParts ) {
			_parts = kitParts.map( ( part ) => {
				return part.slug;
			} );
		}

		let _templates = [];
		if ( kitTemplates ) {
			_templates = kitTemplates.map( ( part ) => {
				return part.slug;
			} );
		}

		let _demoPages = [];
		if ( kitDemoPages ) {
			_demoPages = kitDemoPages.map( ( part ) => {
				return part.slug;
			} );
		}

		updateState( {
			activeParts: _parts,
			activeTemplates: _templates,
			activeDemoPages: _demoPages,
			activeSettings: !! kitSettings,
			activeStyle: !! kitStyles,
		} );
	}, [] );

	return <>
		<Modal position="top"
			className={ classnames(
				'gutenify-component-modal-alert',
				'gutenify-component-modal-hide-header',
				( isApplied && isSuccess ) && 'gutenify-component-modal-alert-auto-width'
			) }
			onRequestClose={ () => {
				onCancel();
			} }
			shouldCloseOnClickOutside={ false }
			__experimentalHideHeader={ true }
			// isFullScreen={ true }
		>
			<div className="components-modal__header">
				<div className="components-modal__header-heading-container">
					<h1
						id="components-modal-header-1"
						className="components-modal__header-heading"
					>
						{ kit.title }{ __( ' Options', '@@text_domain' ) }
					</h1>
				</div>
			</div>
			{ ! isApplied && ! isLoading && <>
				<Card>
					<CardHeader>
						{ __( 'Please choose following options you want to apply.', '@@text_domain' ) }
					</CardHeader>
					<CardBody>
						{ activeParts && kitParts && <div className="gutenify-import-list-template">
							<h4>Template Parts</h4>
							<p className="gutenify-component__help">Choose template parts to replace/ add.</p>
							<ul>{ kitParts.map( ( part ) => {
								const isActive = activeParts.includes( part.slug );
								return <li key={ `part-${ part.slug }` }>
									<ToggleControl
										label={ part.title }
										// help={
										// 	isActive
										// 		? `Replace/ Add Current template part: ${ part.title }.`
										// 		: `Do not import template part: ${ part.title }.`
										// }
										checked={ isActive }
										onChange={ () => {
											let _parts = activeParts;
											if ( isActive ) {
												_parts = _parts.filter( function( value ) {
													return value !== part.slug;
												} );
											} else {
												_parts.push( part.slug );
											}

											updateState( {
												activeParts: _parts,
											} );
										} }
									/>
								</li>;
							} ) }</ul> </div> }
						{ kitTemplates && <div className="gutenify-import-list-template">
							<h4>Templates</h4>
							<p className="gutenify-component__help">Choose template to replace/ add.</p>
							<ul>{ kitTemplates.map( ( item ) => {
								const isActive = activeTemplates.includes( item.slug );
								return <li key={ `part-${ item.slug }` }>
									<ToggleControl
										label={ item.title }
										// help={
										// 	isActive
										// 		? `Replace/ Add Current template: ${ item.title }.`
										// 		: `Do not import template: ${ item.title }.`
										// }
										checked={ isActive }
										onChange={ () => {
											let _items = activeTemplates;
											if ( isActive ) {
												_items = _items.filter( function( value ) {
													return value !== item.slug;
												} );
											} else {
												_items.push( item.slug );
											}

											updateState( {
												activeTemplates: _items,
											} );
										} }
									/>
								</li>;
							} ) }</ul> </div> }
						{ kitDemoPages && <div className="gutenify-import-list-template">
							<h4>Demo Pages</h4>
							<p className="gutenify-component__help">Choose pages to add.</p>
							<ul>{ kitDemoPages.map( ( item ) => {
								const isActive = activeDemoPages.includes( item.slug );
								return <li key={ `part-${ item.slug }` }>
									<ToggleControl
										label={ item.title }
										checked={ isActive }
										onChange={ () => {
											let _items = activeDemoPages;
											if ( isActive ) {
												_items = _items.filter( function( value ) {
													return value !== item.slug;
												} );
											} else {
												_items.push( item.slug );
											}

											updateState( {
												activeDemoPages: _items,
											} );
										} }
									/>
								</li>;
							} ) }</ul> </div> }
						{ kitDemoPages && <div className="gutenify-import-list-template">
							<h4>{ __( 'Style & Setting' ) }</h4>
							<p className="gutenify-component__help">{ __( 'Choose replace style & setting.' ) }</p>
							<ul>
								<li >
									<ToggleControl
										label={ __( 'Style' ) }
										checked={ !! activeStyle }
										onChange={ () => {
											updateState( {
												activeStyle: ! activeStyle,
											} );
										} }
									/>
								</li>
								<li >
									<ToggleControl
										label={ __( 'Settings' ) }
										checked={ !! activeSettings }
										onChange={ () => {
											updateState( {
												activeSettings: ! activeSettings,
											} );
										} }
									/>
								</li>
							</ul>
						</div> }
					</CardBody>
					<CardBody>
						<div className="gutenify-import-instructions">
							<Icon icon="warning" className="gutenify-icon-color-warning" /> Please backup your site before applying kit.
						</div>
						<ExternalLink href="#">{ __( 'Need Help?' ) }</ExternalLink>
					</CardBody>
					<CardFooter>
						<div className="gutenify-component-modal-buttons">
							<Button isSecondary onClick={ () => {
								onCancel();
							} }>Cancel</Button>
							<Button isPrimary onClick={ async () => {
								updateState( {
									isLoading: true,
								} );
								const isKitApplied = await onApply( {
									activeParts,
									activeTemplates,
									activeDemoPages,
									activeSettings,
									activeStyle,
								} );
								if ( isKitApplied ) {
									updateState( {
										isSuccess: true,
										isApplied: true,
										isLoading: false,
									} );
								} else {
									updateState( {
										isSuccess: false,
										isApplied: true,
										isLoading: false,
									} );
								}
							} }>Apply Kit</Button>

						</div>
					</CardFooter>
				</Card>

			</> }

			{ isLoading && ! isApplied && <>
				<div className="gutenify-component-modal-success-message">
					<Spinner />
					<h1>Please wait. Kit is being applied..</h1>
				</div>
			</> }
			{ isApplied && isSuccess && <>
				<div className="gutenify-component-modal-success-message">

					{ ( activeParts.length > 0 || activeTemplates.length > 0 || activeDemoPages.length > 0 || activeStyle || activeSettings ) ? <><Icon icon="yes-alt" size={ 50 } className="gutenify-component-modal-success-message-icon" /> <h1>Kit Applied successfully</h1>
						<ul className="gutenify-kit-imported-list">
							{ activeParts.length > 0 && <li><Icon icon="yes" /> { __( 'Imported template parts.' ) }</li> }
							{ activeTemplates.length > 0 && <li><Icon icon="yes" /> { __( 'Imported templates.' ) }</li> }
							{ activeDemoPages.length > 0 && <li><Icon icon="yes" /> { __( 'Imported demo pages.' ) }</li> }
							{ activeStyle && <li><Icon icon="yes" /> { __( 'Imported styles.' ) }</li> }
							{ activeSettings && <li><Icon icon="yes" /> { __( 'Imported settings.' ) }</li> }
						</ul>
						<Button isPrimary onClick={ () => {
							location.reload();
						} }>Close</Button></>
						: <><Icon icon="warning" size={ 50 } className="gutenify-icon-color-warning" /><h1>Nothing has been imported.</h1>
							<Button isPrimary onClick={ () => {
								onCancel();
							} }>Close</Button></>
					}

				</div>
			</> }

		</Modal>
	</>;
};
export default AlertKitApply;
